import { useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { DrawerBodyWrapper, TOAST_TYPE } from '@base'
import { Button, ErrorMessage, Input, Label } from '@baseV2'
import { MODAL_TYPES, strings } from '@constants'
import { mergeTenderBookBidders, updateTenderBookBidderPan } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleModal } from '@data/state/action/root'
import { Spacings, Text } from '@styles'

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_4B};

	& + & {
		margin-top: ${Spacings.SPACING_6B};
		padding-top: ${Spacings.SPACING_6B};
		border-top: 1px solid ${({ theme }) => theme.colors.border.default};
	}
`

const SectionTitle = styled.h3`
	font-size: ${Text.MEDIUM};
	font-weight: ${Text.BOLD_600};
	color: ${({ theme }) => theme.colors.text.primary};
`

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_1B};
`

// Input + action button on one line; inputs here carry no label/error so the button stretches to the input's height.
const ActionRow = styled.div`
	display: flex;
	align-items: stretch;
	gap: ${Spacings.SPACING_2B};

	& > button {
		flex-shrink: 0;
	}
`

const ManageBidder = ({ data, update }) => {
	const dispatch = useDispatch()
	// Merge matches bidders on `org_name`, which can differ from the display `organisation_name`.
	const parentOrgName = data?.org_name ?? ''

	const [savedPan, setSavedPan] = useState(data?.pan ?? '')
	const [pan, setPan] = useState(data?.pan ?? '')
	const [mergedOrgNames, setMergedOrgNames] = useState([])
	const [childOrgName, setChildOrgName] = useState('')

	const isValidPan = PAN_REGEX.test(pan)
	// Only flag the format once something has been typed; an empty field just keeps Update disabled.
	const panError = pan && !isValidPan ? strings('enter_valid_pan') : null

	const trimmedChildOrgName = childOrgName.trim()
	const getMergeError = () => {
		const name = trimmedChildOrgName.toLowerCase()
		if (!name) return null
		if (name === parentOrgName.trim().toLowerCase()) return 'Cannot merge an organisation into itself'
		if (mergedOrgNames.some((merged) => merged.toLowerCase() === name)) return 'This organisation is already merged'
		return null
	}
	const mergeError = getMergeError()

	const confirmThen = (title, action) => {
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title,
				confirmAction: (actionType) => {
					dispatch(toggleModal(false))
					if (actionType) action()
				},
			})
		)
	}

	const onPanUpdate = () => {
		if (!isValidPan || !data?.bidder_detail_id) return
		confirmThen(strings(['confirm', 'to', 'update', 'pan']), () => {
			dispatch(showLoader(true))
			updateTenderBookBidderPan(data.bidder_detail_id, pan)
				.then(() => {
					dispatch(showToast(true, 'PAN updated successfully', { type: TOAST_TYPE.SUCCESS }))
					setSavedPan(pan)
					update?.()
				})
				.catch((err) => {
					dispatch(showToast(true, err?.response?.data?.message || 'Failed to update PAN', { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => dispatch(showLoader(false)))
		})
	}

	const onMerge = () => {
		if (!trimmedChildOrgName || mergeError || !parentOrgName) return
		confirmThen(`Merge "${trimmedChildOrgName}" into "${parentOrgName}"?`, () => {
			dispatch(showLoader(true))
			mergeTenderBookBidders(parentOrgName, trimmedChildOrgName)
				.then(() => {
					dispatch(showToast(true, 'Organisations merged successfully', { type: TOAST_TYPE.SUCCESS }))
					// Lock the merged name in place and open a fresh input beneath it.
					setMergedOrgNames((prev) => [...prev, trimmedChildOrgName])
					setChildOrgName('')
					update?.()
				})
				.catch((err) => {
					dispatch(showToast(true, err?.response?.data?.message || 'Failed to merge organisations', { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => dispatch(showLoader(false)))
		})
	}

	return (
		<DrawerBodyWrapper>
			<Section>
				<SectionTitle>Update PAN</SectionTitle>
				<Input id='bidder-org-name' label={strings('organisation', 'name')} value={data?.organisation_name || '-'} disabled />
				<Field>
					<Label htmlFor='bidder-pan'>{`${strings('pan')} *`}</Label>
					<ActionRow>
						<Input
							id='bidder-pan'
							placeholder='Enter PAN'
							value={pan}
							maxLength={10}
							onChange={(e) => setPan(e.target.value.trim().toUpperCase())}
						/>
						<Button color='primary' disabled={!isValidPan || pan === savedPan} onClick={onPanUpdate}>
							{strings('update')}
						</Button>
					</ActionRow>
					{panError && <ErrorMessage>{panError}</ErrorMessage>}
				</Field>
			</Section>

			<Section>
				<SectionTitle>Merge Organisations</SectionTitle>
				<Input id='merge-parent-org-name' label='Parent Organisation' value={parentOrgName || '-'} disabled />
				<Field>
					<Label htmlFor='merge-child-org-name'>Organisations to merge</Label>
					{mergedOrgNames.map((name) => (
						<ActionRow key={name}>
							<Input value={name} disabled />
							<Button color='primary' disabled>
								Merged
							</Button>
						</ActionRow>
					))}
					<ActionRow>
						<Input
							id='merge-child-org-name'
							placeholder='Enter organisation name'
							value={childOrgName}
							onChange={(e) => setChildOrgName(e.target.value.trimStart())}
						/>
						<Button color='primary' disabled={!trimmedChildOrgName || !!mergeError || !parentOrgName} onClick={onMerge}>
							Merge
						</Button>
					</ActionRow>
					{mergeError && <ErrorMessage>{mergeError}</ErrorMessage>}
				</Field>
			</Section>
		</DrawerBodyWrapper>
	)
}

export default ManageBidder
